import { useEffect, useState } from "react";
import { Film } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCategories } from "@/hooks/useCategories";
import { useVenues } from "@/hooks/useVenues";
import { toLocalDatetimeInput, fromLocalDatetimeInput } from "@/lib/utils";
import type { StagedEvent } from "@/types";

interface StagedEventSheetProps {
  event: StagedEvent | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApprove: (values: {
    venueId?: string;
    categoryId?: string;
    destination: "events" | "movies";
    title: string;
    eventDescription: string;
    date: string;
    registrationType: string;
    registrationValue: string;
  }) => void;
  onReject: () => void;
  isPending: boolean;
}

export function StagedEventSheet({
  event,
  open,
  onOpenChange,
  onApprove,
  onReject,
  isPending,
}: StagedEventSheetProps) {
  const { data: categories = [] } = useCategories();
  const { data: venues = [] } = useVenues();

  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [venueId, setVenueId] = useState("");
  const [eventDescription, setEventDescription] = useState("");
  const [registrationType, setRegistrationType] = useState("");
  const [registrationValue, setRegistrationValue] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [destination, setDestination] = useState<"events" | "movies">("events");

  useEffect(() => {
    if (!open || !event) return;

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setTitle(event.title);
    setDate(toLocalDatetimeInput(event.date));
    setVenueId("");
    setEventDescription(event.eventDescription);
    setRegistrationType(event.registrationType ?? "");
    setRegistrationValue(event.registrationValue ?? "");

    const defaultDestination = event.enriched ? "movies" : "events";
    setDestination(defaultDestination);
    setCategoryId("");
  }, [open, event]);

  const handleApprove = () => {
    onApprove({
      venueId: venueId || undefined,
      ...(destination === "events" ? { categoryId } : {}),
      destination,
      title,
      eventDescription,
      date: fromLocalDatetimeInput(date) ?? new Date().toISOString(),
      registrationType,
      registrationValue,
    });
  };

  const visibleVenues = venues.filter((v) => !v.isHidden);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>{event?.title ?? "Wydarzenie"}</SheetTitle>
        </SheetHeader>

        <div className="flex-1 space-y-4 py-4">
          {event && event.imageUrls.length > 0 ? (
            <img
              src={event.imageUrls[0]}
              alt={event.title}
              className="w-full h-48 object-cover rounded-md"
            />
          ) : (
            <div className="w-full h-48 rounded-md bg-slate-100 flex items-center justify-center">
              <Film className="h-8 w-8 text-slate-400" />
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="sheet-title">Tytuł</Label>
            <Input id="sheet-title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="sheet-date">Data</Label>
            <Input
              id="sheet-date"
              type="datetime-local"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label>Lokalizacja</Label>
            <Select value={venueId} onValueChange={setVenueId}>
              <SelectTrigger>
                <SelectValue placeholder="Domyślna (OCK)" />
              </SelectTrigger>
              <SelectContent>
                {visibleVenues.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="sheet-description">Opis</Label>
            <Textarea
              id="sheet-description"
              rows={4}
              value={eventDescription}
              onChange={(e) => setEventDescription(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label>Typ zapisu</Label>
            <Select
              value={registrationType || "none"}
              onValueChange={(v) => {
                setRegistrationType(v === "none" ? "" : v);
                if (v === "none") setRegistrationValue("");
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Brak" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Brak</SelectItem>
                <SelectItem value="ticket_url">Link do biletu</SelectItem>
                <SelectItem value="phone">Telefon</SelectItem>
                <SelectItem value="email">E-mail</SelectItem>
                <SelectItem value="url">URL</SelectItem>
                <SelectItem value="other">Inne</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {registrationType && (
            <div className="space-y-1.5">
              <Label htmlFor="sheet-registration-value">Wartość zapisu</Label>
              <Input
                id="sheet-registration-value"
                value={registrationValue}
                onChange={(e) => setRegistrationValue(e.target.value)}
                placeholder={
                  registrationType === "phone"
                    ? "+48 123 456 789"
                    : registrationType === "email"
                      ? "kontakt@example.com"
                      : "https://..."
                }
              />
            </div>
          )}

          {event &&
            (event.metadata.imdbRating ||
              event.metadata.rtScore ||
              event.metadata.filmwebUrl ||
              event.metadata.filmwebRating ||
              event.metadata.year ||
              event.metadata.runtime) && (
              <div className="space-y-1.5 pt-3 border-t">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Dane filmu
                </p>
                {event.metadata.originalTitle && (
                  <p className="text-sm text-slate-700">
                    Tytuł oryginalny: {event.metadata.originalTitle}
                  </p>
                )}
                {event.metadata.year && (
                  <p className="text-sm text-slate-700">Rok: {event.metadata.year}</p>
                )}
                {event.metadata.runtime && (
                  <p className="text-sm text-slate-700">Czas: {event.metadata.runtime} min</p>
                )}
                {event.metadata.filmwebRating && (
                  <p className="text-sm text-slate-700">
                    Filmweb: {event.metadata.filmwebRating}/10
                  </p>
                )}
                {event.metadata.imdbRating && (
                  <p className="text-sm text-slate-700">IMDB: {event.metadata.imdbRating}/10</p>
                )}
                {event.metadata.rtScore && (
                  <p className="text-sm text-slate-700">
                    Rotten Tomatoes: {event.metadata.rtScore}
                  </p>
                )}
                {event.metadata.filmwebUrl && (
                  <a
                    href={event.metadata.filmwebUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline block"
                  >
                    Filmweb →
                  </a>
                )}
              </div>
            )}

          <div className="space-y-1.5">
            <Label>Miejsce docelowe</Label>
            <Select
              value={destination}
              onValueChange={(v) => setDestination(v as "events" | "movies")}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="movies">Film (Kino)</SelectItem>
                <SelectItem value="events">Wydarzenie</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {destination === "events" && (
            <div className="space-y-1.5">
              <Label>Kategoria</Label>
              <Select value={categoryId} onValueChange={setCategoryId}>
                <SelectTrigger>
                  <SelectValue placeholder="Wybierz kategorię" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <SheetFooter className="gap-2 sm:gap-0">
          <Button variant="destructive" onClick={onReject} disabled={isPending}>
            {isPending && <Spinner className="h-4 w-4 mr-2" />}
            Odrzuć
          </Button>
          <Button
            className="bg-green-600 hover:bg-green-700"
            onClick={handleApprove}
            disabled={isPending}
          >
            {isPending && <Spinner className="h-4 w-4 mr-2" />}
            Zatwierdź i zapisz
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
